import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { StudioPage, Panel, Badge, Loading, Empty, cx } from "./StudioShared";
import { CheckCircle2, XCircle, BarChart3 } from "lucide-react";

const PRESETS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["quarter", "This Quarter"], ["year", "This Year"]];
const CATS = ["AI Intelligence", "Business Intelligence", "Integrations"];

const fmtVal = (m) => {
  if (m.value == null || m.value === "—") return "—";
  if (m.fmt === "usd") return `$${Number(m.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (m.fmt === "pct") return `${m.value}%`;
  if (m.fmt === "ms") return Number(m.value) < 1000 ? `${m.value}ms` : `${(m.value / 1000).toFixed(1)}s`;
  return typeof m.value === "number" ? m.value.toLocaleString() : m.value;
};
const TONE = { gold: "text-gold", emerald: "text-emerald-400", amber: "text-amber-400", red: "text-red-400" };

export default function ExecutiveIntelligence() {
  const [preset, setPreset] = useState("month");
  const [cat, setCat] = useState(CATS[0]);
  const [panels, setPanels] = useState(null);

  const load = useCallback(async () => {
    setPanels(null);
    try { const r = await api.get(`/exec/dashboard?category=${encodeURIComponent(cat)}&preset=${preset}`); setPanels(r.data.panels || []); }
    catch { setPanels([]); }
  }, [cat, preset]);
  useEffect(() => { load(); }, [load]);

  return (
    <StudioPage
      title="Executive Intelligence"
      question="Monitor every subsystem — measure, compare, and improve continuously."
      testid="executive-intelligence"
      actions={
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="ei-preset"
          className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      }
    >
      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="ei-tabs">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`ei-tab-${c.replace(/\s+/g, "-").toLowerCase()}`}
            className={cx("px-3 py-1.5 text-xs rounded-sm border transition-colors",
              cat === c ? "bg-gold text-navy border-gold font-medium" : "border-white/10 text-slate-400 hover:text-white")}>
            {c}
          </button>
        ))}
      </div>

      {!panels ? <Loading label="Loading dashboards…" /> : panels.length === 0 ? <Empty text="No panels in this category." testid="ei-empty" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ei-panels">
          {panels.map((p) => (
            <Panel key={p.domain} className="p-4" testid={`ei-panel-${p.domain}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 size={15} className="text-gold" />
                  <span className="text-white text-sm font-semibold">{p.title}</span>
                </div>
                {p.connected ? (
                  <span className="flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`ei-connected-${p.domain}`}><CheckCircle2 size={12} /> connected</span>
                ) : (
                  <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid={`ei-disconnected-${p.domain}`}><XCircle size={12} /> not connected</span>
                )}
              </div>
              {p.note && <div className="text-[11px] text-slate-500 mb-3 -mt-1">{p.note}</div>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(p.metrics || []).map((m) => (
                  <div key={m.key} className="border border-white/10 rounded-sm p-2.5" data-testid={`ei-metric-${p.domain}-${m.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{m.label}</div>
                    <div className={cx("text-sm mt-0.5 font-medium", TONE[m.tone] || "text-white")}>{fmtVal(m)}</div>
                    {m.sub && <div className="text-[10px] text-slate-600 mt-0.5">{m.sub}</div>}
                  </div>
                ))}
              </div>
              {(p.breakdown || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1" data-testid={`ei-breakdown-${p.domain}`}>
                  {p.breakdown.slice(0, 6).map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-300 truncate">{b.label || b.provider}</span>
                      <span className="text-slate-400">{b.value != null ? (typeof b.value === "number" ? b.value.toLocaleString() : b.value) : `${b.requests ?? ""} req · ${b.success_rate ?? ""}%`}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          ))}
        </div>
      )}
    </StudioPage>
  );
}
